import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiCalendar,
  FiClock,
  FiAlertCircle,
  FiPlus,
  FiEdit3,
  FiX,
  FiSave,
  FiTrash2,
  FiBell,
  FiCheckCircle,
  FiFilter,
  FiDollarSign,
  FiAward,
  FiExternalLink
} = FiIcons;

function ScholarshipDeadlines() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingDeadline, setEditingDeadline] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('deadline');

  const [deadlineForm, setDeadlineForm] = useState({
    title: '',
    school: '',
    amount: '',
    deadline: '',
    applicationUrl: '',
    requirements: [],
    notes: '',
    status: 'not-started',
    priority: 'medium',
    reminderDays: 7
  });

  const [newRequirement, setNewRequirement] = useState('');

  const loadDeadlines = () => {
    try {
      const saved = localStorage.getItem('athletepath_deadlines');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading deadlines:', error);
    }
    return [];
  };

  const [deadlines, setDeadlines] = useState(loadDeadlines());

  useEffect(() => {
    try {
      localStorage.setItem('athletepath_deadlines', JSON.stringify(deadlines));
    } catch (error) {
      console.error('Error saving deadlines:', error);
    }
  }, [deadlines]);

  const getDaysUntil = (deadline) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getUrgencyColor = (deadline, status) => {
    if (status === 'completed') return 'bg-green-100 text-green-800 border-green-200';
    const days = getDaysUntil(deadline);
    if (days < 0) return 'bg-red-100 text-red-800 border-red-200';
    if (days <= 7) return 'bg-orange-100 text-orange-800 border-orange-200';
    if (days <= 14) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-blue-100 text-blue-800 border-blue-200';
  };

  const getUrgencyText = (deadline, status) => {
    if (status === 'completed') return 'Completed';
    const days = getDaysUntil(deadline);
    if (days < 0) return `${Math.abs(days)} days overdue`;
    if (days === 0) return 'Due today!';
    if (days === 1) return 'Due tomorrow';
    return `${days} days left`;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800';
      case 'not-started':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getFilteredDeadlines = () => {
    let filtered = [...deadlines];

    if (filterStatus !== 'all') {
      if (filterStatus === 'upcoming') {
        filtered = filtered.filter((d) => {
          const days = getDaysUntil(d.deadline);
          return days >= 0 && d.status !== 'completed';
        });
      } else if (filterStatus === 'overdue') {
        filtered = filtered.filter((d) => {
          const days = getDaysUntil(d.deadline);
          return days < 0 && d.status !== 'completed';
        });
      } else if (filterStatus === 'completed') {
        filtered = filtered.filter((d) => d.status === 'completed');
      }
    }

    filtered.sort((a, b) => {
      if (sortBy === 'deadline') {
        return new Date(a.deadline) - new Date(b.deadline);
      } else if (sortBy === 'amount') {
        const amountA = parseInt(a.amount.replace(/[^0-9]/g, ''));
        const amountB = parseInt(b.amount.replace(/[^0-9]/g, ''));
        return amountB - amountA;
      } else if (sortBy === 'status') {
        const statusOrder = { 'not-started': 0, 'in-progress': 1, completed: 2 };
        return statusOrder[a.status] - statusOrder[b.status];
      }
      return 0;
    });

    return filtered;
  };

  const handleAddDeadline = () => {
    setEditingDeadline(null);
    setDeadlineForm({
      title: '',
      school: '',
      amount: '',
      deadline: '',
      applicationUrl: '',
      requirements: [],
      notes: '',
      status: 'not-started',
      priority: 'medium',
      reminderDays: 7
    });
    setShowAddModal(true);
  };

  const handleEditDeadline = (deadline) => {
    setEditingDeadline(deadline.id);
    setDeadlineForm({ ...deadline });
    setShowAddModal(true);
  };

  const handleSaveDeadline = () => {
    if (!deadlineForm.title || !deadlineForm.deadline) {
      alert('Please fill in at least the scholarship title and deadline');
      return;
    }

    if (editingDeadline) {
      setDeadlines(
        deadlines.map((deadline) =>
          deadline.id === editingDeadline
            ? { ...deadlineForm, id: editingDeadline }
            : deadline
        )
      );
    } else {
      const newDeadline = {
        ...deadlineForm,
        id: Math.max(...deadlines.map((d) => d.id), 0) + 1,
        createdAt: new Date().toISOString().split('T')[0]
      };
      setDeadlines([...deadlines, newDeadline]);
    }

    setShowAddModal(false);
    setEditingDeadline(null);
  };

  const handleDeleteDeadline = (deadlineId) => {
    if (window.confirm('Are you sure you want to delete this scholarship deadline?')) {
      setDeadlines(deadlines.filter((deadline) => deadline.id !== deadlineId));
    }
  };

  const handleStatusChange = (deadlineId, newStatus) => {
    setDeadlines(
      deadlines.map((deadline) => {
        if (deadline.id === deadlineId) {
          const updates = { status: newStatus };
          if (newStatus === 'completed' && !deadline.completedDate) {
            updates.completedDate = new Date().toISOString().split('T')[0];
          } else if (newStatus !== 'completed') {
            updates.completedDate = null;
          }
          return { ...deadline, ...updates };
        }
        return deadline;
      })
    );
  };

  const addRequirement = () => {
    if (newRequirement.trim()) {
      setDeadlineForm({
        ...deadlineForm,
        requirements: [...deadlineForm.requirements, newRequirement.trim()]
      });
      setNewRequirement('');
    }
  };

  const removeRequirement = (index) => {
    setDeadlineForm({
      ...deadlineForm,
      requirements: deadlineForm.requirements.filter((_, i) => i !== index)
    });
  };

  const filteredDeadlines = getFilteredDeadlines();
  const upcomingCount = deadlines.filter((d) => {
    const days = getDaysUntil(d.deadline);
    return days >= 0 && d.status !== 'completed';
  }).length;
  const overdueCount = deadlines.filter((d) => {
    const days = getDaysUntil(d.deadline);
    return days < 0 && d.status !== 'completed';
  }).length;
  const completedCount = deadlines.filter((d) => d.status === 'completed').length;
  const totalAmount = deadlines.reduce((sum, d) => {
    return sum + parseInt(d.amount.replace(/[^0-9]/g, ''));
  }, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg sm:text-xl font-space-grotesk font-semibold text-secondary">
            Scholarship Deadlines
          </h2>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            Track and manage your scholarship application deadlines
          </p>
        </div>
        <button
          onClick={handleAddDeadline}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors btn-primary flex items-center justify-center space-x-2 text-sm"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          <span>Add Deadline</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm border border-gray-100">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <SafeIcon icon={FiCalendar} className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
            </div>
            <div className="min-w-0">
              <div className="text-xl sm:text-2xl font-bold text-blue-600 truncate">{upcomingCount}</div>
              <div className="text-xs sm:text-sm text-gray-600">Upcoming</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm border border-gray-100">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <SafeIcon icon={FiAlertCircle} className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
            </div>
            <div className="min-w-0">
              <div className="text-xl sm:text-2xl font-bold text-red-600 truncate">{overdueCount}</div>
              <div className="text-xs sm:text-sm text-gray-600">Overdue</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm border border-gray-100">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <SafeIcon icon={FiCheckCircle} className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
            </div>
            <div className="min-w-0">
              <div className="text-xl sm:text-2xl font-bold text-green-600 truncate">{completedCount}</div>
              <div className="text-xs sm:text-sm text-gray-600">Completed</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm border border-gray-100">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <SafeIcon icon={FiDollarSign} className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600" />
            </div>
            <div className="min-w-0">
              <div className="text-xl sm:text-2xl font-bold text-yellow-600 truncate">
                ${(totalAmount / 1000).toFixed(0)}k
              </div>
              <div className="text-xs sm:text-sm text-gray-600">Total Value</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="space-y-4">
          {/* Filter Label and Buttons */}
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiFilter} className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span className="text-sm font-medium text-gray-700">Filter:</span>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {[
              { value: 'all', label: 'All' },
              { value: 'upcoming', label: 'Upcoming' },
              { value: 'overdue', label: 'Overdue' },
              { value: 'completed', label: 'Completed' }
            ].map((filter) => (
              <button
                key={filter.value}
                onClick={() => setFilterStatus(filter.value)}
                className={`px-3 py-1.5 text-xs sm:text-sm rounded-full transition-colors whitespace-nowrap ${
                  filterStatus === filter.value
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {/* Sort By */}
          <div className="flex items-center space-x-2 pt-2 border-t border-gray-100">
            <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="flex-1 sm:flex-none px-3 py-1.5 text-xs sm:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="deadline">Deadline</option>
              <option value="amount">Amount</option>
              <option value="status">Status</option>
            </select>
          </div>
        </div>
      </div>

      {/* Deadlines List */}
      <div className="space-y-4">
        {filteredDeadlines.length === 0 ? (
          <div className="bg-white rounded-xl p-8 sm:p-12 shadow-sm border border-gray-100 text-center">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiCalendar} className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
            </div>
            <h3 className="text-lg sm:text-xl font-space-grotesk font-semibold text-secondary mb-2">
              No Deadlines Found
            </h3>
            <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
              {filterStatus === 'all'
                ? 'Start tracking your scholarship deadlines to stay organized.'
                : 'No deadlines match your current filter.'}
            </p>
            {filterStatus === 'all' && (
              <button
                onClick={handleAddDeadline}
                className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors btn-primary text-sm sm:text-base"
              >
                Add Your First Deadline
              </button>
            )}
          </div>
        ) : (
          filteredDeadlines.map((deadline, index) => (
            <motion.div
              key={deadline.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start space-x-3 flex-1 min-w-0">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={FiAward} className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-space-grotesk font-semibold text-secondary break-words">
                        {deadline.title}
                      </h3>
                      <p className="text-sm sm:text-base text-gray-600 break-words">{deadline.school}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleEditDeadline(deadline)}
                      className="text-gray-400 hover:text-primary transition-colors"
                      title="Edit deadline"
                    >
                      <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteDeadline(deadline.id)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      title="Delete deadline"
                    >
                      <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`px-3 py-1 text-xs rounded-full font-medium border whitespace-nowrap ${getUrgencyColor(
                      deadline.deadline,
                      deadline.status
                    )}`}
                  >
                    {getUrgencyText(deadline.deadline, deadline.status)}
                  </span>
                  <span className={`px-3 py-1 text-xs rounded-full capitalize whitespace-nowrap ${getPriorityColor(deadline.priority)}`}>
                    {deadline.priority} priority
                  </span>
                  <span className="px-3 py-1 text-xs bg-green-100 text-green-800 rounded-full font-semibold whitespace-nowrap">
                    {deadline.amount}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-600">
                    <SafeIcon icon={FiCalendar} className="w-4 h-4 flex-shrink-0" />
                    <span>Deadline: {new Date(deadline.deadline).toLocaleDateString()}</span>
                  </div>
                  {deadline.applicationUrl && (
                    <a
                      href={deadline.applicationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-2 text-xs sm:text-sm text-primary hover:underline"
                    >
                      <SafeIcon icon={FiExternalLink} className="w-4 h-4 flex-shrink-0" />
                      <span>Application Link</span>
                    </a>
                  )}
                </div>

                {deadline.requirements.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                    <h4 className="text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                      Requirements:
                    </h4>
                    <ul className="space-y-1">
                      {deadline.requirements.map((req, idx) => (
                        <li
                          key={idx}
                          className="text-xs sm:text-sm text-gray-600 flex items-start space-x-2"
                        >
                          <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                          <span className="break-words">{req}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {deadline.notes && (
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-xs sm:text-sm text-blue-800 break-words">{deadline.notes}</p>
                  </div>
                )}

                <div className="flex items-center space-x-3">
                  <select
                    value={deadline.status}
                    onChange={(e) => handleStatusChange(deadline.id, e.target.value)}
                    className={`px-3 py-1.5 text-xs rounded-full capitalize cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary ${getStatusColor(
                      deadline.status
                    )}`}
                  >
                    <option value="not-started">Not Started</option>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Reminder Info Box */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
        <div className="flex items-start space-x-3">
          <SafeIcon icon={FiBell} className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-gray-800 mb-1">Stay on Track</h4>
            <p className="text-sm text-gray-700">
              Set reminder notifications for each deadline to ensure you never miss an important
              application. We recommend starting applications at least 2-3 weeks before the deadline
              to allow time for gathering materials and revisions.
            </p>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showAddModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-50"
              onClick={() => setShowAddModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl sm:text-2xl font-space-grotesk font-bold text-secondary">
                      {editingDeadline
                        ? 'Edit Scholarship Deadline'
                        : 'Add New Scholarship Deadline'}
                    </h2>
                    <button
                      onClick={() => setShowAddModal(false)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <SafeIcon icon={FiX} className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Scholarship Title *
                        </label>
                        <input
                          type="text"
                          value={deadlineForm.title}
                          onChange={(e) =>
                            setDeadlineForm({ ...deadlineForm, title: e.target.value })
                          }
                          placeholder="e.g., NCAA Division I Basketball Scholarship"
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          School/Organization
                        </label>
                        <input
                          type="text"
                          value={deadlineForm.school}
                          onChange={(e) =>
                            setDeadlineForm({ ...deadlineForm, school: e.target.value })
                          }
                          placeholder="e.g., UCLA Bruins"
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Scholarship Amount
                        </label>
                        <input
                          type="text"
                          value={deadlineForm.amount}
                          onChange={(e) =>
                            setDeadlineForm({ ...deadlineForm, amount: e.target.value })
                          }
                          placeholder="e.g., $25,000"
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Application Deadline *
                        </label>
                        <input
                          type="date"
                          value={deadlineForm.deadline}
                          onChange={(e) =>
                            setDeadlineForm({ ...deadlineForm, deadline: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Priority Level
                        </label>
                        <select
                          value={deadlineForm.priority}
                          onChange={(e) =>
                            setDeadlineForm({ ...deadlineForm, priority: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Application URL
                        </label>
                        <input
                          type="url"
                          value={deadlineForm.applicationUrl}
                          onChange={(e) =>
                            setDeadlineForm({ ...deadlineForm, applicationUrl: e.target.value })
                          }
                          placeholder="https://..."
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Requirements
                      </label>
                      <div className="flex items-center space-x-2 mb-3">
                        <input
                          type="text"
                          value={newRequirement}
                          onChange={(e) => setNewRequirement(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && addRequirement()}
                          placeholder="Add a requirement..."
                          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                        <button
                          onClick={addRequirement}
                          className="bg-primary text-white p-2 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <SafeIcon icon={FiPlus} className="w-5 h-5" />
                        </button>
                      </div>
                      {deadlineForm.requirements.length > 0 && (
                        <div className="space-y-2">
                          {deadlineForm.requirements.map((req, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between bg-gray-50 px-4 py-2 rounded-lg"
                            >
                              <span className="text-sm text-gray-700">{req}</span>
                              <button
                                onClick={() => removeRequirement(index)}
                                className="text-red-500 hover:text-red-700 transition-colors"
                              >
                                <SafeIcon icon={FiX} className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                      <textarea
                        value={deadlineForm.notes}
                        onChange={(e) =>
                          setDeadlineForm({ ...deadlineForm, notes: e.target.value })
                        }
                        placeholder="Add any additional notes or reminders..."
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col-reverse sm:flex-row items-center justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                    <button
                      onClick={() => setShowAddModal(false)}
                      className="w-full sm:w-auto px-6 py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveDeadline}
                      className="w-full sm:w-auto bg-primary text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors btn-primary flex items-center justify-center space-x-2"
                    >
                      <SafeIcon icon={FiSave} className="w-4 h-4" />
                      <span>{editingDeadline ? 'Update Deadline' : 'Add Deadline'}</span>
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

export default ScholarshipDeadlines;